import {
  Autocomplete,
  AutocompleteProps,
  TextFieldProps as TextFieldPropsType,
  ChipProps as ChipPropsType,
  CircularProgress,
  TextField,
} from '@mui/material';
import { Fragment, forwardRef, ForwardedRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AstroResponsibleUserItem } from '@/generated/gql/graphql';

import { AstronautUserAutocompleteProps, useAstronautUserAutocomplete } from './AstonautUserAutocomplete.hook';
import { GroupItems, OverlineText } from './AstonautUserAutocomplete.styled';

const ChipProps: ChipPropsType = { variant: 'outlined' };

export interface UserAutocompleteProps<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean = true,
> extends AstronautUserAutocompleteProps<Multiple, DisableClearable, FreeSolo> {
  TextFieldProps?: Partial<TextFieldPropsType>;
  className?: string;
  placeholder?: string;
  error?: boolean;
}

const AstronautUserAutocompleteInner = <
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean = true,
>(
  {
    TextFieldProps,
    onChange,
    value: valueProp,
    capabilityIds,
    commodityGroupIds,
    size = 'small',
    multiple,
    hasEmptyOption,
    translationPrefix,
    className,
    placeholder,
    error,
    ...props
  }: UserAutocompleteProps<Multiple, DisableClearable, FreeSolo>,
  ref: ForwardedRef<unknown>,
) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'AstronautAutocomplete' });

  const { loading, ...autocompleteProps } = useAstronautUserAutocomplete<Multiple, DisableClearable, FreeSolo>({
    value: valueProp,
    multiple,
    capabilityIds,
    commodityGroupIds,
    hasEmptyOption,
    translationPrefix,
    onChange,
  });

  const elementProps = {
    size,
    openOnFocus: true,
    filterSelectedOptions: true,
    noOptionsText: t('noOptions'),
    loadingText: t('loading'),
    renderInput: (
      params: Parameters<
        NonNullable<AutocompleteProps<AstroResponsibleUserItem, false, false, true>['renderInput']>
      >[0],
    ) => (
      <TextField
        {...params}
        error={error}
        placeholder={placeholder}
        {...TextFieldProps}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <Fragment>
              {loading ? <CircularProgress size={20} /> : null}
              {params.InputProps.endAdornment}
            </Fragment>
          ),
        }}
      />
    ),
    renderGroup: (
      params: Parameters<
        NonNullable<AutocompleteProps<AstroResponsibleUserItem, false, false, true>['renderGroup']>
      >[0],
    ) => (
      <li key={params.key}>
        <OverlineText variant="overline">{t(params.group)}</OverlineText>
        <GroupItems>{params.children}</GroupItems>
      </li>
    ),
    className,
    slotProps: { chip: ChipProps },
    ...autocompleteProps,
    ...props,
  } as unknown as AutocompleteProps<AstroResponsibleUserItem, false, false, true>;

  return <Autocomplete<AstroResponsibleUserItem, false, false, true> ref={ref} {...elementProps} />;
};

export const AstronautUserAutocomplete = forwardRef(AstronautUserAutocompleteInner) as <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
>(
  props: UserAutocompleteProps<Multiple, DisableClearable> & {
    ref?: ForwardedRef<unknown>;
  },
) => ReturnType<typeof AstronautUserAutocompleteInner>;
